import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { RecordData, Service } from 'webapp-types';
import type { SourceStatusLevel as StatusLevel } from 'webapp/enums/enums';
import { ModelName, SelectSourceOptionType, SourceStatusLevel } from 'webapp/enums/enums';
import { getFormattedTime, getRelativeTime } from 'webapp/utils/number';
import type { VerificationInfo } from 'webapp/utils/verification';

import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type SourceType from 'webapp/models-custom/source-type';
import StatusObject, {
	StatusObjectAction,
	StatusObjectType,
} from 'webapp/models-custom/status-object';
import type { DataSourceDatasetsMetadata } from 'webapp/models/source';
import type SourceRecord from 'webapp/models/source';
import { DataSourceSyncStatus, DataSourceSyncType, type Connection } from 'webapp/models/source';

import { Severity } from 'webapp/services/logger';
import type MetricMoverService from 'webapp/services/metric-mover';

export default class Source extends BufferedModel<SourceRecord, RecordData.Source.Serialized> {
	@service('logger') declare loggerService: Service.Logger;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('metric-mover') declare metricMoverService: MetricMoverService;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked extraClasses = ''; // Used for animations in app\components\metric-library\grid\index.ts
	@tracked childSources: Source[] = [];

	// tell it apart from a ConnectRequest / ConnectSelection in the datamanager-connected list
	declare isConnectRequest?: false;
	declare isConnectSelection?: false;

	// ------- props from / to ED record ------

	get id() {
		return this.model.id;
	}

	get type(): number {
		return this.model.type;
	}

	get title(): string {
		return this.getBuffered('title');
	}

	set title(value: string) {
		this.setBuffered('title', value);
	}

	get subtitle(): string {
		return this.model.subtitle;
	}

	get avatar(): string {
		return this.model.avatar;
	}

	get statusLevel(): StatusLevel | '' {
		return this.model.statusLevel;
	}

	get statusTitle(): string {
		return this.model.statusTitle;
	}

	get statusDescription(): string {
		return this.model.statusDescription;
	}

	get statusAction(): StatusObjectAction | '' | null {
		return this.model.statusAction;
	}

	get statusType(): StatusObjectType | '' | null {
		return this.model.statusType;
	}

	get statusUserResponseLink(): string {
		return this.model.statusUserResponseLink;
	}

	get wrapperFor(): string | null {
		return this.model.wrapperFor;
	}

	get created(): number {
		return this.model.created;
	}

	get createdById(): number {
		return this.model.createdById;
	}

	get ownerId(): number {
		return this.model.ownerId;
	}

	get token(): string {
		return this.model.token;
	}

	get isRoot(): boolean {
		return this.model.isRoot;
	}

	get isLocked(): boolean {
		return this.model.isLocked;
	}

	get users(): number[] {
		return this.model.users;
	}

	get timezone(): string {
		return this.model.timezone;
	}

	get initialTitle(): string {
		return this.model.initialTitle;
	}

	get customSyncInterval(): number {
		return this.model.customSyncInterval;
	}

	get connection(): Connection | undefined {
		return this.model.connection;
	}

	get selectedSourceOption(): SelectSourceOptionType | undefined {
		return this.model.selectedSourceOption;
	}

	get datasetsMetadata(): DataSourceDatasetsMetadata | undefined {
		return this.model.datasetsMetadata;
	}

	get verificationInfo(): VerificationInfo | undefined {
		return this.model.verificationInfo;
	}

	// ------- other props ------

	override get validationRules() {
		return {
			title: ['required'],
		};
	}

	@cached
	get sourceType() {
		return this.mmSourcesService.findSourceTypeForSourceRecord(this.model)!;
	}

	get sourceTypePopularity() {
		return this.sourceType.popularity;
	}

	get isGenericCustom() {
		return this.sourceType.isToken;
	}

	get isCustomRESTApi() {
		return this.sourceType.isCustomRESTApi;
	}

	get isDataset() {
		return this.selectedSourceOption === SelectSourceOptionType.DATASETS;
	}

	get isMergedDataset() {
		return this.selectedSourceOption === SelectSourceOptionType.MERGED;
	}

	get isTypeOfDataset() {
		return (
			this.selectedSourceOption &&
			[SelectSourceOptionType.MERGED, SelectSourceOptionType.DATASETS].includes(
				this.selectedSourceOption,
			)
		);
	}

	get isTypeIngest() {
		return this.model.isTypeIngest;
	}

	get isDatasetSyncInProgress() {
		return (
			this.lastSync?.syncType &&
			[DataSourceSyncType.INITIAL_SYNC, DataSourceSyncType.MODIFICATION_REQUEST].includes(
				this.lastSync.syncType,
			) &&
			this.lastSync.syncStatus === DataSourceSyncStatus.IN_PROGRESS
		);
	}

	get isDatasetPartialSync() {
		return this.lastSync?.syncStatus === DataSourceSyncStatus.PARTIAL_SYNC;
	}

	get isDatasetWithAction() {
		return (
			this.isDataset &&
			(this.statusObject.isActionFinishSetup || this.statusObject.isActionRefetch)
		);
	}

	get isSyncProgressBlockingUsage() {
		return (
			this.isDatasetSyncInProgress &&
			this.lastSync?.syncType !== DataSourceSyncType.SCHEDULED_UPDATE
		);
	}

	get isSourceDisabledForUsage() {
		const isSourceStatusError = this.statusLevel === SourceStatusLevel.ERROR;
		const isSyncProgressBlockingSelection = this.isSyncProgressBlockingUsage;
		const isActionRefetch = this.statusObject.isActionRefetch;

		return (
			isSourceStatusError ||
			(this.isTypeOfDataset && (isSyncProgressBlockingSelection || isActionRefetch))
		);
	}

	get sourceDisabledForUsageInfo(): string {
		if (!this.isSourceDisabledForUsage) {
			return '';
		}

		const statusTitle = this.isDatasetSyncInProgress
			? 'Metric data source sync is in progress'
			: !this.lastSync?.lastSuccessfulSyncAt
				? 'Metric data source contains no data or has yet to be prepared'
				: this.statusTitle;
		const statusDescription =
			'Your metric cannot be edited until the data source status is resolved.';

		return `<b>${statusTitle}</b><br>${statusDescription}`;
	}

	get isDatasetAndDisabledForFilterSelection() {
		return (
			this.isDataset &&
			(this.statusLevel === SourceStatusLevel.ERROR ||
				this.isSyncProgressBlockingUsage ||
				this.isDatasetWithAction)
		);
	}

	get isAbstract() {
		return this.selectedSourceOption === SelectSourceOptionType.ABSTRACT;
	}

	@cached
	get statusObject() {
		return StatusObject.createFromSource(this);
	}

	get isPushable() {
		return this.token && !this.wrapperFor;
	}

	get isWrapper() {
		return !this.type && !!this.wrapperFor;
	}

	get titleAndSubtitle() {
		let title = this.title;

		const subtitle = this.subtitle;
		if (subtitle && subtitle !== title) {
			title = subtitle + ' > ' + title;
		}

		return title;
	}

	get statusDescriptionWithLinks() {
		return this.statusObject?.descriptionWithLinks;
	}

	get statusActionCapitalized() {
		return this.statusObject?.statusActionCapitalized;
	}

	get canReconnect() {
		return (
			!!this.statusObject?.canReconnect &&
			!!this.connection?.canReconnect &&
			this.haveBasicPermissions &&
			!this.isConnectionPrivate
		);
	}

	get canSetupSource() {
		return (
			!!this.statusObject?.isActionSourceConnected &&
			this.haveBasicPermissions &&
			!!this.connection?.canReconnect
		);
	}

	get canReauthorize() {
		return (
			!!this.statusObject?.canReauthorize &&
			!!this.connection?.canReconnect &&
			!!this.haveBasicPermissions &&
			!this.isConnectionPrivate
		);
	}

	get canEditTimezone() {
		return this.sourceType.timezone === null || this.sourceType.isWithCustomToken;
	}

	get isBillingLink() {
		return this.currentUserService.isAdmin && !!this.statusObject?.isActionBilling;
	}

	get canChooseOnOnboarding() {
		return (
			this.statusObject &&
			!this.statusObject?.isError &&
			this.selectedSourceOption !== SelectSourceOptionType.ABSTRACT
		);
	}

	get isShownAsDataError() {
		const statusObject = this.statusObject;
		return !!statusObject?.isError || !!statusObject?.isActionVersion;
	}

	get statusClass() {
		let statusClass = this.statusObject?.level;
		if (this.statusObject?.isDatasetError) {
			statusClass = 'error';
		}
		return `status-${statusClass}`;
	}

	get statusIconClass() {
		return this.statusObject?.statusIconClass;
	}

	get isStatusActionable() {
		return this.statusObject?.isStatusActionable;
	}

	get isDatasetStatus() {
		return this.isDataset && !this.isStatusActionable;
	}

	get isDatasetInError() {
		return this.isDataset && this.statusObject?.isDatasetError;
	}

	get hasBasicMetrics() {
		return Source.hasBasicMetrics(this.sourceType);
	}

	get isLegacyCustomSource(): boolean {
		return !!this.sourceType?.isLegacyCustomSourceType && !this.isTypeOfDataset;
	}

	get isLegacyCreationBlocked(): boolean {
		return this.metricMoverService.isLegacyDeprecationActive && this.isLegacyCustomSource;
	}

	get canAccessMetricMover(): boolean {
		if (!this.metricMoverService.isLegacyDeprecationActive) return false;
		return this.currentUserService.isAdmin || Boolean(this.isOwnedByMe);
	}

	get hasLegacyMetricsToMigrate(): boolean {
		if (!this.canAccessMetricMover) return false;
		if (!this.isLegacyCustomSource) return false;
		return (
			this.metrics.length > 0 &&
			this.metrics.some((m) => m.isTypeCustomQuery || m.isTypeCustomPush)
		);
	}

	get countBasicMetrics() {
		let metricCount = 0;
		if (this.hasBasicMetrics) {
			metricCount = this.sourceType.basicMetricRecords.length || 0;
		}
		return metricCount;
	}

	get realAvatar() {
		return this.avatar || this.sourceType.avatar;
	}

	get isScripts() {
		return !this.type && !this.isRoot && this.isLocked;
	}

	get isSqlConnector() {
		return this.sourceType.isSqlConnector;
	}

	get isSqlishConnector() {
		return this.sourceType.isSqlishConnector;
	}

	get isSQLType() {
		return this.sourceType.isSQLType;
	}

	get isSheets() {
		return this.sourceType.isSheets;
	}

	get showInitialTitle() {
		return this.initialTitle && this.initialTitle !== this.title;
	}

	@cached
	get createdBy() {
		let user = null;

		const createdById = this.createdById;
		if (createdById) {
			user = this.mmUsersService.findRecord(String(createdById));
			if (!user) {
				this.loggerService.logError(
					'Missing createdBy for source',
					{
						sourceId: this.id,
						userId: createdById,
					},
					Severity.WARNING,
				);
			}
		}
		return user;
	}

	@cached
	get createdByText() {
		const user = this.createdBy;
		return user?.name || '/';
	}

	get createdRelative() {
		return getRelativeTime(this.created);
	}

	get createdTimeText() {
		return getFormattedTime(this.created, this.currentUserService.model?.timezone);
	}

	get createdText() {
		return `<span>${this.createdTimeText}</span><br /> by ${this.createdByText}`;
	}

	get createdTextWithoutBreak() {
		return `${this.createdTimeText} by ${this.createdByText}`;
	}

	@cached
	get metrics() {
		return this.mmMetricsService.getMetricsForSpaceAccessId(this.numericId!);
	}

	get syncInterval() {
		return this.customSyncInterval ?? this.billingService.features.dataRefreshInterval;
	}

	get dataSyncText() {
		const syncInterval = this.syncInterval;

		if (syncInterval === 60) {
			return 'Hourly';
		} else if (syncInterval === 1440) {
			return 'Daily';
		}

		if (syncInterval % 60 === 0) {
			return `${syncInterval / 60} hours`;
		}

		return `${syncInterval} minutes`;
	}

	get isPremiumDataSync() {
		return this.syncInterval <= 15;
	}

	get showDataSyncUpgrade() {
		let showUpgrade;
		if (this.customSyncInterval) {
			showUpgrade = false; // don't show upgrade if we have custom sync interval set
		} else {
			showUpgrade = this.billingService.showDataSyncUpgrade;
		}
		return showUpgrade;
	}

	@cached
	get userObjs() {
		const users = this.users;
		return this.mmUsersService.savedRecords.filter((user) => {
			return users.includes(+user.id!);
		});
	}

	get userObjsCount() {
		return this.userObjs.length;
	}

	// Used on data manager screen for sorting
	get statusObjectLevel() {
		if (this.statusObject?.isError) {
			return 3;
		} else if (this.statusObject?.isOnHold) {
			return 2;
		} else {
			return 1;
		}
	}

	get isMine() {
		const createdById = this.createdById;
		return createdById && createdById === this.currentUserService.numericId;
	}

	get isOwnedByMe() {
		const ownerId = this.ownerId;
		return ownerId && ownerId === this.currentUserService.numericId;
	}

	get isConnectionPrivate() {
		return this.connection?.permissionStatus === null;
	}

	get canResolveActionableStatus() {
		if (
			this.statusAction &&
			[
				StatusObjectAction.RECONNECT,
				StatusObjectAction.REAUTHORIZE,
				StatusObjectAction.SOURCE_CONNECTED,
			].includes(this.statusAction)
		) {
			return (
				!this.isConnectionPrivate &&
				((this.statusAction === StatusObjectAction.RECONNECT && this.canReconnect) ||
					(this.statusAction === StatusObjectAction.REAUTHORIZE && this.canReauthorize) ||
					(this.statusAction === StatusObjectAction.SOURCE_CONNECTED &&
						this.canSetupSource))
			);
		} else {
			return true;
		}
	}

	get haveBasicPermissions() {
		return this.currentUserService.isAdmin || this.isMine;
	}

	get hasReauthenticatePermissions() {
		return (
			this.connection?.canReconnect &&
			!this.sourceType.isWithCustomToken &&
			!this.isConnectionPrivate
		);
	}

	@cached
	get permissionsEveryone() {
		return this.mmUsersService.savedRecords.length === this.userObjs.length;
	}

	@cached
	get permissionsJustMe() {
		const userObjs = this.userObjs;
		return userObjs.length === 1 && userObjs[0]?.id === this.currentUserService.id;
	}

	get permissionColor() {
		let color = '';
		if (this.permissionsEveryone) {
			color = 'green';
		} else if (this.permissionsJustMe) {
			color = 'red';
		} else if (this.userObjs.length || !this.haveBasicPermissions) {
			color = 'orange';
		}
		return color;
	}

	get permissionIcon() {
		let icon: IconName | undefined;
		if (this.permissionsEveryone) {
			icon = 'Company';
		} else {
			icon = 'User';
		}
		return icon;
	}

	@cached
	get permissionText() {
		const userObjs = this.userObjs,
			myId = this.currentUserService.id!,
			isMe = userObjs.map((item) => item.id).includes(myId);

		let pplCount = userObjs.length,
			addXOthers = false,
			text = '';

		if (!this.haveBasicPermissions) {
			return 'Shared with me';
		}

		if (this.permissionsEveryone) {
			return 'All users';
		}
		if (isMe) {
			pplCount--;
			if (pplCount) {
				text = 'Me & ';
				addXOthers = pplCount > 1;
			} else {
				text = 'Only me';
			}
		}
		if (pplCount && !addXOthers) {
			// get name of first other recipient
			let tmpUser = userObjs[0];
			if (tmpUser?.id === myId) {
				tmpUser = userObjs[1];
			}
			if (tmpUser) {
				text += tmpUser.name;
			}
			pplCount--;
			if (pplCount) {
				text += ' & ';
				addXOthers = true;
			}
		}
		if (addXOthers) {
			text += pplCount + ' other' + (pplCount > 1 ? 's' : '');
		}
		if (!text) {
			text = 'nobody';
		}
		return text;
	}

	@cached
	get permissionTooltip() {
		return this.userObjs.map((item) => item.name).join('<br />');
	}

	get parentInfo() {
		return this.model.parentInfo;
	}

	get lastSync() {
		return this.model.lastSync;
	}

	get status() {
		return this.model.status;
	}

	get infoForFilter() {
		return this.isDataset ? this.parentInfo?.title : this.connection?.name;
	}

	static hasBasicMetrics(sourceType: SourceType) {
		return sourceType && sourceType.hasBasicMetrics && !sourceType.isDb;
	}

	static canReconnect(source: { statusAction: StatusObjectAction }) {
		return [
			StatusObjectAction.RECONNECT,
			StatusObjectAction.SELECT,
			StatusObjectAction.VERSION,
		].includes(source.statusAction);
	}

	static override modelName = ModelName.SOURCE; // store record type
}
